/**
 * PipelineSection.js — Reusable colored-border section for WHEN/PAY/THEN
 *
 * Renders a left-bordered section with a label, chip row, and
 * an [+ Add ...] button. Used three times in ActionPipeline.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import ChipRow from '../chips/ChipRow';
import TypePickerGrid from '../pickers/TypePickerGrid';

export const COLOR_SCHEMES = {
  when: {
    border: 'border-l-2 border-blue-600',
    bg: 'bg-blue-900/10',
    chipBg: 'bg-blue-900/20',
    chipText: 'text-blue-300',
    addBtnText: 'text-blue-400',
    addBtnBorder: 'border-blue-700',
  },
  pay: {
    border: 'border-l-2 border-amber-600',
    bg: 'bg-amber-900/10',
    chipBg: 'bg-amber-900/20',
    chipText: 'text-amber-300',
    addBtnText: 'text-amber-400',
    addBtnBorder: 'border-amber-700',
  },
  then: {
    border: 'border-l-2 border-green-600',
    bg: 'bg-green-900/10',
    chipBg: 'bg-green-900/20',
    chipText: 'text-green-300',
    addBtnText: 'text-green-400',
    addBtnBorder: 'border-green-700',
  },
};

const PipelineSection = ({
  title,
  items,
  colorScheme,
  onAdd,
  onUpdate,
  onRemove,
  chipType,
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const safeItems = items || [];

  const handleAdd = (newItem) => {
    onAdd(newItem);
    setShowPicker(false);
  };

  return (
    <div className={cn('rounded-lg p-3', colorScheme.border, colorScheme.bg)}>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
        {title}
      </div>

      <ChipRow
        items={safeItems}
        chipType={chipType}
        colorScheme={colorScheme}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />

      {showPicker ? (
        <TypePickerGrid
          chipType={chipType}
          onSelect={handleAdd}
          onCancel={() => setShowPicker(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className={cn(
            'mt-2 inline-flex items-center gap-1 px-3 py-1 text-sm rounded-md',
            'border border-dashed',
            colorScheme.addBtnBorder,
            colorScheme.addBtnText,
            'hover:bg-gray-800/50 transition-colors'
          )}
        >
          <Plus size={14} />
          Add {chipType}
        </button>
      )}
    </div>
  );
};

export default PipelineSection;
